import { useTranslation } from "react-i18next";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import { focusedControlOpenState, focusedControlWrap } from "../stage/focused-overlay-styles";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ConnectionDetailsBody, useQualitySections } from "../stage/ConnectionDetailsBody";
import { QualityIcon, qualityColor } from "./QualityIcon";

export function ViewerConnectionQuality() {
  const { t } = useTranslation();
  const controller = useRoomController();
  const open = useRoomState((s) => s.quality.viewerQualityOpen);
  const visible = useRoomState((s) => s.quality.viewerQualityVisible);
  const label =
    useRoomState((s) => s.quality.viewerQualityLabel) || t("common.screen").toUpperCase();
  const details = useRoomState((s) => s.quality.viewerQualityDetails);
  const reset = useRoomState((s) => s.quality.viewerQualityReset);
  const section = useQualitySections(reset);
  const severity = details?.overview?.severity ?? "info";
  const title = details?.overview
    ? details.overview.titleKey
      ? t(details.overview.titleKey)
      : details.overview.title
    : t("focused.connectionQuality");
  // The name leads with the visible label so it matches what is on screen.
  const accessibleName = `${label} · ${t("focused.connectionQuality")}: ${title}`;
  return (
    // The shared overlay-use marker preserves hover and keyboard idle holds.
    <div
      id="viewerQualityWrap"
      data-focused-overlay-use
      className={focusedControlWrap}
      hidden={!visible}
    >
      <Popover
        open={open}
        onOpenChange={(value) => controller.setQualityPopoverOpen("viewer", value)}
      >
        <PopoverTrigger asChild>
          <Button
            id="viewerQualityAction"
            variant="ghost"
            size="sm"
            data-quality={severity}
            aria-label={accessibleName}
            title={title}
            className={`${focusedControlOpenState} dark:hover:bg-accent/40 dark:data-[state=open]:bg-accent/40 ${qualityColor[severity]}`}
          >
            <QualityIcon severity={severity} />
            <span
              id="focusDetailsMeta"
              className="text-xs font-extrabold whitespace-nowrap text-popover-foreground"
            >
              {label}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          id="viewerQualityPopover"
          aria-labelledby="viewerQualityTitle"
          aria-describedby="viewerQualityDescription"
          side="bottom"
          align="start"
          sideOffset={4}
          alignOffset={-4}
          collisionPadding={8}
          className="max-h-[calc(100dvh-7rem)] w-96 max-w-[calc(100vw-1rem)] overflow-y-auto"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <ConnectionDetailsBody kind="viewer" details={details} {...section} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
